import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ServerEndpoints} from '../../../../server.endpoints';
import {
  FiscalJobCreateRequest,
  FiscalJobDetail,
  FiscalJobKind,
  FiscalJobSummary
} from '../../_models/fiscal-jobs/fiscal-job.models';

/**
 * The typed HTTP surface of the fiscal job engine (#768) — four calls, no state.
 *
 * ### Why this is separate from the store
 *
 * {@link FiscalJobStore} owns *when* a run is read and what the screen holds; this owns *what goes on the
 * wire*. Splitting them is what lets the URL contract be pinned by a spec that stands up no signals and no
 * hub, and it keeps the store testable with a fake of this instead of an `HttpTestingController` dance.
 *
 * ### No shard parameter, deliberately
 *
 * The server derives the tenant from the session's own claims: a location-scoped session sees its location's
 * runs, a company-scoped one (a company owner, and from #767 a tax adviser) sees the whole company's. There is
 * nothing here a caller could pass to widen that, which is the point.
 */
@Injectable({providedIn: 'root'})
export class FiscalJobHttpService {

  constructor(@Inject('BASE_URL') private baseUrl: string, private httpClient: HttpClient) {
  }

  /**
   * Starts a run. Answers as soon as it is durable and scheduled — it does not wait for the work.
   *
   * @param request the kind of work and the entries to cover
   */
  create(request: FiscalJobCreateRequest): Observable<FiscalJobDetail> {
    return this.httpClient.post<FiscalJobDetail>(`${this.baseUrl}${ServerEndpoints.Fiscal_Jobs_Create}`, request);
  }

  /**
   * This tenant's runs, newest first.
   *
   * @param kind only this kind, or omitted for all of them
   * @param take page size (clamped server-side)
   * @param skip how many to skip
   */
  list(kind?: FiscalJobKind, take = 50, skip = 0): Observable<FiscalJobSummary[]> {
    let params = new HttpParams().set('take', take).set('skip', skip);
    if (kind) params = params.set('kind', kind);

    return this.httpClient.get<FiscalJobSummary[]>(
      `${this.baseUrl}${ServerEndpoints.Fiscal_Jobs_List}`, {params});
  }

  /** One run with its per-entry outcomes — what a push is answered with. */
  get(jobId: string): Observable<FiscalJobDetail> {
    return this.httpClient.get<FiscalJobDetail>(
      `${this.baseUrl}${ServerEndpoints.Fiscal_Jobs_Get}`, {params: new HttpParams().set('jobId', jobId)});
  }

  /**
   * Re-runs one entry of a run, on its own.
   *
   * Never the whole run: re-running it would re-do the entries that already succeeded, which for a fiscal
   * record is a second filing of the same document rather than wasted effort.
   */
  retryItem(jobId: string, itemId: string): Observable<FiscalJobDetail> {
    const params = new HttpParams().set('jobId', jobId).set('itemId', itemId);
    return this.httpClient.post<FiscalJobDetail>(
      `${this.baseUrl}${ServerEndpoints.Fiscal_Jobs_RetryItem}`, {}, {params});
  }
}
